import { useState, useEffect } from 'react';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { Save, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../lib/toast';

interface StopWord {
  id: string;
  word: string;
  language: 'uk' | 'ru' | 'en';
  created_at: string;
}

export function StopWordsPage() {
  const [loading, setLoading] = useState(true);
  const [stopWords, setStopWords] = useState<StopWord[]>([]);
  const [newWord, setNewWord] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<'uk' | 'ru' | 'en'>('uk');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadStopWords();
  }, []);

  const loadStopWords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseClient
        .from('stop_words')
        .select('*')
        .order('word', { ascending: true });

      if (error) throw error;
      setStopWords(data || []);
    } catch (err) {
      console.error('Error loading stop words:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stop words');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWord = async () => {
    if (!newWord.trim()) {
      toast.error('Please enter a word');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabaseClient
        .from('stop_words')
        .insert([
          { word: newWord.toLowerCase().trim(), language: selectedLanguage }
        ]);

      if (error) throw error;

      toast.success('Word added successfully');
      setNewWord('');
      await loadStopWords();
    } catch (err) {
      console.error('Error adding stop word:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to add word');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWord = async (id: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this word?');
    if (!confirmed) return;

    try {
      const { error } = await supabaseClient
        .from('stop_words')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Word deleted successfully');
      await loadStopWords();
    } catch (err) {
      console.error('Error deleting stop word:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete word');
    }
  };

  if (loading) return <LoadingScreen />;

  const filteredWords = stopWords.filter(word => word.language === selectedLanguage);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Stop Words Management</h2>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <div className="mb-6">
          <div className="flex gap-4 mb-4">
            <button
              onClick={() => setSelectedLanguage('uk')}
              className={`px-4 py-2 rounded-md ${
                selectedLanguage === 'uk'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ukrainian
            </button>
            <button
              onClick={() => setSelectedLanguage('ru')}
              className={`px-4 py-2 rounded-md ${
                selectedLanguage === 'ru'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Russian
            </button>
            <button
              onClick={() => setSelectedLanguage('en')}
              className={`px-4 py-2 rounded-md ${
                selectedLanguage === 'en'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              English
            </button>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Enter new stop word"
              className="flex-1 px-4 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              onClick={handleAddWord}
              disabled={saving || !newWord.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Word
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredWords.map((word) => (
            <div
              key={word.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <span className="text-sm font-medium text-gray-900">{word.word}</span>
              <button
                onClick={() => handleDeleteWord(word.id)}
                className="text-red-600 hover:text-red-900"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {filteredWords.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No stop words found for {selectedLanguage === 'uk' ? 'Ukrainian' : selectedLanguage === 'ru' ? 'Russian' : 'English'} language
          </div>
        )}
      </div>
    </div>
  );
}